/**
 * Chart Data Extractors
 * Functions for extracting and transforming data for performance charts
 */

import { formatNum } from '@/util/number';

/**
 * Format duration in seconds to HH:MM:SS
 * @param {number|string} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return 'N/A';

  // Parse to number and floor to remove decimals
  const totalSeconds = Math.floor(parseFloat(seconds));

  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return 'N/A';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Extract RPM series data from raw RPM data
 * @param {Object} rpmData - Raw RPM data from API
 * @returns {Array} Array of RPM data points sorted by timestamp
 */
export function extractRpmSeries(rpmData) {
  if (!rpmData || !rpmData.data) return [];

  return Object.entries(rpmData.data)
    .map(([timestamp, data]) => ({
      timestamp: parseInt(timestamp),
      datetime: data.PORT?.datetime || data.STARBOARD?.datetime || '',
      portRpm: data.PORT?.rpm || 0,
      starboardRpm: data.STARBOARD?.rpm || 0,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Extract flowmeter (fuel consumption) series data from raw flowmeter data
 * @param {Object} flowmeterData - Raw flowmeter data from API
 * @returns {Array} Array of fuel consumption data points sorted by timestamp
 */
export function extractFlowmeterSeries(flowmeterData) {
  if (!flowmeterData || !flowmeterData.data) return [];

  return Object.entries(flowmeterData.data)
    .map(([timestamp, data]) => ({
      timestamp: parseInt(timestamp),
      datetime: data.PORT_IN?.datetime || '',
      portFuelCons: data.PORT_FuelCons || 0,
      starboardFuelCons: data.STARBOARD_FuelCons || 0,
      totalFuelCons: data.ME_FuelCons || 0,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Extract GPS series data (speed and wave height) from raw GPS data
 * @param {Object} gpsData - Raw GPS data from API
 * @returns {Array} Array of GPS data points sorted by timestamp
 */
export function extractGpsSeries(gpsData) {
  if (!gpsData || !gpsData.data) return [];

  return Object.entries(gpsData.data)
    .map(([timestamp, data]) => ({
      timestamp: parseInt(timestamp),
      datetime: data.datetime || '',
      speed: data.speed ? data.speed * 1.94384 : 0, // Convert to knots
      waveHeight: data.waveHeight || 0,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Prepare donut chart data from engine operation mode data
 * @param {Object} engineData - Engine operation mode data
 * @returns {Array} Array of mode data for donut chart
 */
export function prepareDonutChartData(engineData) {
  if (!engineData) return [];

  return Object.entries(engineData)
    .map(([modeName, data]) => ({
      mode: modeName,
      value: data.runningSeconds || 0,
    }))
    .filter((d) => d.value > 0);
}

/**
 * Prepare operation mode table items
 * @param {Object} engineData - Engine operation mode data
 * @returns {Array} Array of formatted table items
 */
export function getOperationModeItems(engineData) {
  if (!engineData) return [];

  const totalRunningSeconds = Object.values(engineData).reduce(
    (sum, data) => sum + (data.runningSeconds || 0),
    0,
  );

  return Object.entries(engineData).map(([modeName, data]) => {
    const percentage = totalRunningSeconds > 0
      ? formatNum((((data.runningSeconds || 0) * 100) / totalRunningSeconds), 2)
      : 0;
    return {
      modeName,
      percentage: `${percentage}%`,
      runningTime: formatDuration(data.runningSeconds),
    };
  });
}

/**
 * Prepare AE running hours table items
 * @param {Object} aeMetrics - AE metrics from summary
 * @returns {Array} Array of formatted table items
 */
export function getAeRunningItems(aeMetrics) {
  if (!aeMetrics?.runningSeconds) return [];

  const runningSeconds = aeMetrics.runningSeconds;
  const totalRunningSeconds = Object.values(runningSeconds).reduce(
    (sum, value) => sum + (value || 0),
    0,
  );

  return Object.entries(runningSeconds).map(([engineName, seconds]) => {
    const percentage = totalRunningSeconds > 0
      ? formatNum((((seconds || 0) * 100) / totalRunningSeconds), 2)
      : 0;
    return {
      engineName,
      percentage: `${percentage}%`,
      runningTime: formatDuration(seconds),
    };
  });
}

/**
 * Prepare AE fuel consumption table items
 * @param {Object} aeMetrics - AE metrics from summary
 * @returns {Array} Array of formatted table items
 */
export function getAeFuelItems(aeMetrics) {
  if (!aeMetrics?.fuelConsumption) return [];

  const fuelConsumption = aeMetrics.fuelConsumption;
  const totalFuel = Object.values(fuelConsumption).reduce(
    (sum, value) => sum + (value || 0),
    0,
  );

  return Object.entries(fuelConsumption).map(([engineName, fuel]) => {
    const percentage = totalFuel > 0
      ? formatNum((((fuel || 0) * 100) / totalFuel), 2)
      : 0;
    return {
      engineName,
      percentage: `${percentage}%`,
      fuelConsumption: `${formatNum(Number(fuel), 2)} L`,
    };
  });
}

/**
 * Prepare wave direction duration table items
 * @param {Object} weatherMetrics - Weather metrics from summary
 * @returns {Array} Array of formatted table items
 */
export function getWaveDurationItems(weatherMetrics) {
  if (!weatherMetrics?.runningSecondsDirection) return [];

  const runningSecondsDirection = weatherMetrics.runningSecondsDirection;
  const totalRunningSeconds = Object.values(runningSecondsDirection).reduce(
    (sum, value) => sum + (value || 0),
    0,
  );

  return Object.entries(runningSecondsDirection).map(([directionName, seconds]) => {
    const percentage = totalRunningSeconds > 0
      ? formatNum((((seconds || 0) * 100) / totalRunningSeconds), 2)
      : 0;
    return {
      directionName: directionName.replace(/([A-Z])/g, ' $1').trim(),
      percentage: `${percentage}%`,
      duration: formatDuration(seconds),
    };
  });
}

/**
 * Prepare combined AE metrics table items (Running Hours + Fuel Consumption)
 * @param {Object} aeMetrics - AE metrics from summary
 * @returns {Array} Array of formatted table items with combined data
 */
export function getAeCombinedItems(aeMetrics) {
  if (!aeMetrics) return [];

  const runningSeconds = aeMetrics.runningSeconds || {};
  const fuelConsumption = aeMetrics.fuelConsumption || {};

  // Get all unique engine names from both data sources
  const allEngines = new Set([
    ...Object.keys(runningSeconds),
    ...Object.keys(fuelConsumption),
  ]);

  // Calculate totals
  const totalRunningSeconds = Object.values(runningSeconds).reduce(
    (sum, value) => sum + (value || 0),
    0,
  );
  const totalFuel = Object.values(fuelConsumption).reduce(
    (sum, value) => sum + (value || 0),
    0,
  );

  return Array.from(allEngines).map((engineName) => {
    const seconds = runningSeconds[engineName] || 0;
    const fuel = fuelConsumption[engineName] || 0;

    const runningPercentage = totalRunningSeconds > 0
      ? formatNum((((seconds || 0) * 100) / totalRunningSeconds), 2)
      : 0;
    const fuelPercentage = totalFuel > 0
      ? formatNum((((fuel || 0) * 100) / totalFuel), 2)
      : 0;

    return {
      engineName,
      runningTime: formatDuration(seconds),
      runningPercentage: `${runningPercentage}%`,
      fuelConsumption: `${formatNum(Number(fuel), 2)} L`,
      fuelPercentage: `${fuelPercentage}%`,
    };
  });
}
